import { Button, FormHelperText, Grid, TextField } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AppSwitch from "components/Shared/AppSwitch";
import RichTextEditor from "components/Shared/RichTextEditor";
import { useGetCountryDDLQuery } from "store/services/countryApi";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

const RegionForm = ({
  regionDetail = null,
  isBusy = false,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();

  // react form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      name: regionDetail?.name ?? "",
      country: regionDetail?.country ?? "",
      code: regionDetail?.code ?? "",
      coordinates: regionDetail?.coordinates || { lat: "", long: "" },
      description: regionDetail?.description ?? "",
      status: regionDetail?.status === "Inactive" ? false : true,
    },
  });

  // RTK Query

  const {
    data: countries,
    isLoading: loadingCountries,
    isFetching: fetchingCountries,
  } = useGetCountryDDLQuery();

  React.useEffect(() => {
    if (regionDetail) {
      reset({
        name: regionDetail.name,
        code: regionDetail.code,
        country: regionDetail.country,
        coordinates: regionDetail.coordinates || { lat: "", long: "" },
        description: regionDetail.description,
        status: regionDetail.status === "Inactive" ? false : true,
      });
    }
  }, [regionDetail, reset]);

  const renderTextField = (name, label, type = "text", rules = {}) => (
    <Grid item md={6}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <TextField
            fullWidth
            label={label}
            type={type}
            size="small"
            {...field}
          />
        )}
      />
    </Grid>
  );

  return (
    <AppForm>
      <AppForm.Content>
        <Grid container spacing={3} paddingY={2}>
          <Grid item md={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <TextField
                  required
                  {...field}
                  fullWidth
                  label="Name"
                  type="text"
                  size="small"
                  error={!!errors?.name}
                  helperText={errors?.name?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="code"
              control={control}
              rules={{
                required: "Code is required",
                maxLength: {
                  value: 8,
                  message: "Code must be at most 8 characters long",
                },
                validate: (value) => {
                  if (value !== value.toUpperCase()) {
                    return "Code must be in uppercase";
                  }
                  if (!/^[A-Z0-9]+$/.test(value)) {
                    return "Code can only contain uppercase letters and numbers";
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  required
                  {...field}
                  fullWidth
                  label="Code"
                  type="text"
                  size="small"
                  error={!!errors?.code}
                  helperText={errors?.code?.message}
                />
              )}
            />
          </Grid>

          {renderTextField("coordinates.lat", "Latitude", "number", {})}
          {renderTextField("coordinates.long", "Longitude", "number", {})}

          <Grid item md={6}>
            <Controller
              name="country"
              control={control}
              rules={{ required: "Country is required" }}
              render={({ field }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Country"
                  loading={loadingCountries || fetchingCountries}
                  loadingText="Loading..."
                  options={countries || []}
                  getOptionLabel={(opt) => opt && opt?.name}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  renderOption={(props, opt) =>
                    opt ? (
                      <li {...props} key={opt._id}>
                        <div className="flex items-center gap-2">
                          <img
                            src={opt.flagUrl?.svg}
                            alt={`${opt.name} flag`}
                            style={{ width: "20px", height: "15px" }}
                          />
                          <p>{opt.name}</p>
                        </div>
                      </li>
                    ) : null
                  }
                  value={field?.value ?? ""}
                  onChange={(_, data) => {
                    setValue("country", data);
                  }}
                  error={!!errors?.country}
                  helperText={errors?.country?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={12}>
            <FormHelperText>Description</FormHelperText>

            <Controller
              name="description"
              control={control}
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  initialValue={
                    regionDetail?.description
                      ? regionDetail?.description
                      : "<p>Enter Note Content.</p>"
                  }
                  onChange={onChange}
                  height={200}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <AppSwitch {...field} label="Is Active?" />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button disabled={isBusy} onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isBusy}
          onClick={handleSubmit((data) => onSubmit(data))}
        >
          {regionDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default RegionForm;
